import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ShieldAlert, LogOut, CreditCard, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SubscriptionRequiredModal = () => {
    const { isSubscriptionModalOpen, setIsSubscriptionModalOpen, user, logout, isDarkMode } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);

    const isExpired = user?.subscriptionStatus === 'expired';

    useEffect(() => {
        if (isSubscriptionModalOpen) {
            const timer = setTimeout(() => setIsVisible(true), 100);
            return () => clearTimeout(timer);
        } else {
            setIsVisible(false);
        }
    }, [isSubscriptionModalOpen]);

    const handleRenew = () => {
        setIsSubscriptionModalOpen(false);
        navigate('/admin/settings/subscription');
    };

    const handleLogout = async () => {
        await logout();
    };

    return (
        <Transition.Root show={isSubscriptionModalOpen} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-[9999]"
                onClose={() => { }} // Non-closable
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-500"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-xl transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-500"
                            enterFrom="opacity-0 translate-y-8 scale-95"
                            enterTo="opacity-100 translate-y-0 scale-100"
                            leave="ease-in duration-300"
                            leaveFrom="opacity-100 translate-y-0 scale-100"
                            leaveTo="opacity-0 translate-y-8 scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-[32px] bg-zinc-900/40 border border-white/10 px-8 pb-10 pt-12 text-center shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-md backdrop-filter backdrop-blur-2xl">
                                {/* Grain Overlay */}
                                <div
                                    className="absolute inset-0 z-0 pointer-events-none opacity-[0.03] mix-blend-overlay"
                                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")` }}
                                />

                                {/* Premium Serif Font Import */}
                                <style>{`
                                    @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap');
                                    .modal-serif { font-family: 'Instrument Serif', Georgia, serif; }
                                `}</style>

                                <div className="relative z-10">
                                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-500/10 border border-red-500/20 mb-8 shadow-[0_0_40px_rgba(239,68,68,0.2)]">
                                        <ShieldAlert className="h-10 w-10 text-red-500 animate-pulse" />
                                    </div>

                                    <Dialog.Title as="h3" className="modal-serif text-4xl font-normal leading-tight text-white mb-4 tracking-tight">
                                        {t('subscription_required')}
                                    </Dialog.Title>

                                    <p className="text-zinc-400 text-lg font-light leading-relaxed mb-10 px-4">
                                        {t('subscription_expired_message')}
                                    </p>

                                    <div className="space-y-3">
                                        <button
                                            type="button"
                                            onClick={handleRenew}
                                            className="group relative w-full flex items-center justify-center gap-2.5 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white text-sm font-medium py-3 px-6 rounded-xl transition-all duration-300 shadow-[0_4px_20px_rgb(220,38,38,0.15)] hover:shadow-[0_4px_25px_rgb(220,38,38,0.3)] hover:-translate-y-0.5"
                                        >
                                            <CreditCard className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                            {t('renew_subscription')}
                                            <ArrowRight className="w-3.5 h-3.5 opacity-50 group-hover:translate-x-1 transition-transform" />
                                        </button>

                                        {!isExpired && (
                                            <button
                                                type="button"
                                                onClick={() => setIsSubscriptionModalOpen(false)}
                                                className="w-full flex items-center justify-center gap-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white text-sm font-normal py-3 px-6 rounded-xl transition-all duration-300 border border-white/5"
                                            >
                                                {t('close') || 'Maybe Later'}
                                            </button>
                                        )}

                                        <button
                                            type="button"
                                            onClick={handleLogout}
                                            className="w-full flex items-center justify-center gap-2.5 bg-white/[0.03] hover:bg-white/[0.08] text-zinc-400 hover:text-white text-sm font-normal py-3 px-6 rounded-xl transition-all duration-300 border border-white/5 hover:border-white/10"
                                        >
                                            <LogOut className="w-4 h-4 opacity-60" />
                                            {t('logout')}
                                        </button>
                                    </div>

                                    <div className="mt-8">
                                        <span className="text-[10px] uppercase tracking-[3px] text-zinc-600 font-medium">
                                            {t('premium_experience_notice')}
                                        </span>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default SubscriptionRequiredModal;
